// =============================================================================
// Portal Orders Page — Organization-scoped order list (admin-style UI/UX)
// =============================================================================

import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams, Link } from 'react-router';
import { Card, Input, Select, Badge, Button, Spinner, TagOverflowList } from '@tkpod/ui';
import { useApiClient } from '@tkpod/api-client';
import { StoreFilter, NO_STORE } from '../components/StoreFilter';
import { useConnections } from '../hooks/use-connections';
import { SettlementBadge } from '../components/order/SettlementBadge';
import {
  WholesaleChargeStatus,
  ORDER_STATUS_MERCHANT_VARIANT,
  getMerchantOrderStatusLabel,
} from '@tkpod/shared';

// Filter values are what the API accepts: internal OrderStatus values plus
// the PROCESSING composite (server-side expands to every status whose badge
// reads "Order Processing" — see MERCHANT_ORDER_PROCESSING_STATUSES).
// Labels are the merchant-facing terms so the dropdown matches the badges.
const ORDER_STATUSES = [
  { value: '', label: 'All Statuses' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'NEW', label: 'New' },
  { value: 'AWAITING_FULFILLMENT_REQUEST', label: 'Awaiting Approval' },
  { value: 'PROCESSING', label: 'Order Processing' },
  { value: 'READY_TO_SHIP', label: 'In Production' },
  { value: 'SHIPPED', label: 'Shipped' },
  { value: 'DELIVERED', label: 'Delivered' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'ON_HOLD', label: 'On Hold' },
];

const FULFILLMENT_REQUESTED_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'true', label: 'Requested' },
  { value: 'false', label: 'Not Requested' },
];

const SOURCE_OPTIONS = [
  { value: '', label: 'All Sources' },
  { value: 'MANUAL', label: 'Manual' },
  { value: 'SHOPIFY', label: 'Shopify' },
  { value: 'WOOCOMMERCE', label: 'WooCommerce' },
  { value: 'API', label: 'API' },
];

const SORT_OPTIONS = [
  { value: 'desc', label: 'Newest First' },
  { value: 'asc', label: 'Oldest First' },
];

function FilterIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function FilterChip({
  label,
  onRemove,
}: {
  label: string;
  onRemove: () => void;
}) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-border bg-surface px-2 py-0.5 text-[11px] font-medium text-text-secondary">
      {label}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className="rounded-full p-0.5 transition-colors hover:bg-surface-secondary"
        aria-label={`Remove filter: ${label}`}
      >
        <XIcon className="h-3 w-3" />
      </button>
    </span>
  );
}

interface Order {
  id: string;
  orderNumber: string;
  customerName: string | null;
  status: string;
  itemCount: number;
  total: string;
  currency: string;
  /**
   * Wholesale total the merchant owes Transfer Kingdom for this order.
   * Null for legacy rows. Decimal string with 2 decimal places.
   */
  wholesaleTotalCost?: string | null;
  /**
   * Wholesale settlement state (what TK collects from the merchant) — distinct
   * from the order `status` and the Shopify customer payment status.
   */
  wholesaleChargeStatus?: string | null;
  /** Order-level fulfillment shape derived from line snapshots. */
  fulfillmentMix?: 'ALL_TK_POD' | 'ALL_SELF' | 'MIXED';
  fulfillmentRequestedAt?: string | null;
  /** Store that produced this order. Null for manually created orders. */
  store?: { id: string; name: string; shopDomain: string | null } | null;
  /** Shopify-synced order tags — rendered as clickable filter chips. */
  tags?: string[];
  createdAt: string;
}

interface OrdersResponse {
  data: Order[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

export function PortalOrdersPage() {
  const api = useApiClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filtersOpen, setFiltersOpen] = useState(false);

  const page = Number(searchParams.get('page') ?? '1');
  const status = searchParams.get('status') ?? '';
  const search = searchParams.get('search') ?? '';
  const sortOrder = searchParams.get('sortOrder') ?? 'desc';
  const dateFrom = searchParams.get('dateFrom') ?? '';
  const dateTo = searchParams.get('dateTo') ?? '';
  const source = searchParams.get('source') ?? '';
  const totalMin = searchParams.get('totalMin') ?? '';
  const totalMax = searchParams.get('totalMax') ?? '';
  const connectionId = searchParams.get('connectionId') ?? '';
  const fulfillmentRequested = searchParams.get('fulfillmentRequested') ?? '';
  const tag = searchParams.get('tag') ?? '';

  const connectionsQuery = useConnections();
  const storeLabel = useMemo(() => {
    if (connectionId === NO_STORE) return 'Manual (no store)';
    const conn = connectionsQuery.data?.find((c) => c.id === connectionId);
    return conn?.name ?? connectionId;
  }, [connectionId, connectionsQuery.data]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (status) count++;
    if (source) count++;
    if (connectionId) count++;
    if (dateFrom) count++;
    if (dateTo) count++;
    if (totalMin) count++;
    if (totalMax) count++;
    if (fulfillmentRequested) count++;
    if (tag) count++;
    return count;
  }, [status, source, connectionId, dateFrom, dateTo, totalMin, totalMax, fulfillmentRequested, tag]);

  const [searchInput, setSearchInput] = useState(search);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [tagInput, setTagInput] = useState(tag);

  useEffect(() => {
    setTagInput(tag);
  }, [tag]);

  const clearAllFilters = useCallback(() => {
    const next = new URLSearchParams();
    next.set('page', '1');
    if (search) next.set('search', search);
    next.set('sortOrder', sortOrder);
    setSearchParams(next);
    setSearchInput(search);
  }, [search, sortOrder, setSearchParams]);

  const setFilter = useCallback(
    (key: string, value: string) => {
      const next = new URLSearchParams(searchParams);
      if (value) {
        next.set(key, value);
      } else {
        next.delete(key);
      }
      if (key !== 'page') next.set('page', '1');
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const setFilterRef = useRef(setFilter);
  setFilterRef.current = setFilter;

  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setFilterRef.current('search', searchInput);
      searchDebounceRef.current = null;
    }, 400);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchInput]);

  const ordersQuery = useQuery({
    queryKey: [
      'portal',
      'orders',
      { page, status, search, sortOrder, dateFrom, dateTo, source, totalMin, totalMax, connectionId, fulfillmentRequested, tag },
    ],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', '20');
      if (status) params.set('status', status);
      if (search) params.set('search', search);
      params.set('sortOrder', sortOrder);
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);
      if (source) params.set('source', source);
      if (totalMin) params.set('totalMin', totalMin);
      if (totalMax) params.set('totalMax', totalMax);
      if (connectionId) params.set('connectionId', connectionId);
      if (fulfillmentRequested) params.set('fulfillmentRequested', fulfillmentRequested);
      if (tag) params.set('tag', tag);
      return api.get<OrdersResponse>(`/api/v1/orders?${params.toString()}`);
    },
    refetchInterval: 25_000,
  });

  const applySearchNow = useCallback(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
    setFilter('search', searchInput);
  }, [searchInput, setFilter]);

  const orders = ordersQuery.data?.data ?? [];
  const pagination = ordersQuery.data?.pagination;

  return (
    <div className="space-y-6">
      <Card padding="none">
        {/* Header — title, count, search, filters, sort, CTA */}
        <div className="flex flex-col gap-4 border-b border-border px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-text-primary">Orders</h2>
            {pagination != null && (
              <p className="mt-0.5 text-xs text-text-tertiary">
                {pagination.total} order{pagination.total !== 1 ? 's' : ''}
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="min-w-[200px] flex-1 sm:max-w-xs">
              <Input
                placeholder="Search orders..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && applySearchNow()}
                aria-label="Search orders by order number or customer"
              />
            </div>
            <button
              type="button"
              onClick={() => setFiltersOpen((v) => !v)}
              className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-2 text-xs font-medium transition-colors ${
                filtersOpen || activeFilterCount > 0
                  ? 'border-brand-500 bg-brand-50 text-brand-700 dark:border-brand-500 dark:bg-brand-500/10 dark:text-brand-400'
                  : 'border-border bg-white text-text-secondary hover:bg-surface-secondary hover:text-text-primary dark:bg-surface dark:hover:bg-surface-secondary'
              }`}
            >
              <FilterIcon className="shrink-0" />
              Filters
              {activeFilterCount > 0 && (
                <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-brand-500 px-1.5 text-[10px] font-semibold text-white">
                  {activeFilterCount}
                </span>
              )}
            </button>
            <div className="w-[160px]">
              <Select
                value={sortOrder}
                onChange={(e) => setFilter('sortOrder', e.target.value)}
                options={SORT_OPTIONS}
                aria-label="Sort orders"
              />
            </div>
            <Link to="/orders/new">
              <Button variant="primary" size="sm">
                Create Order
              </Button>
            </Link>
          </div>
        </div>

        {/* Expanded filter panel */}
        {filtersOpen && (
          <div className="border-b border-border bg-surface-secondary/30 px-5 py-4">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
              <Select
                label="Status"
                value={status}
                onChange={(e) => setFilter('status', e.target.value)}
                options={ORDER_STATUSES}
              />
              <Select
                label="Source"
                value={source}
                onChange={(e) => setFilter('source', e.target.value)}
                options={SOURCE_OPTIONS}
              />
              <StoreFilter
                label="Store"
                value={connectionId}
                onChange={(v) => setFilter('connectionId', v)}
                includeManual
              />
              <Select
                label="Fulfillment requested"
                value={fulfillmentRequested}
                onChange={(e) => setFilter('fulfillmentRequested', e.target.value)}
                options={FULFILLMENT_REQUESTED_OPTIONS}
              />
              <Input
                label="Tag"
                placeholder="Filter by tag..."
                value={tagInput}
                maxLength={40}
                onChange={(e) => setTagInput(e.target.value)}
                onBlur={() => setFilter('tag', tagInput.trim())}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') setFilter('tag', tagInput.trim());
                }}
              />
              <Input
                label="Date from"
                type="date"
                lang="en"
                value={dateFrom}
                onChange={(e) => setFilter('dateFrom', e.target.value)}
              />
              <Input
                label="Date to"
                type="date"
                lang="en"
                value={dateTo}
                onChange={(e) => setFilter('dateTo', e.target.value)}
              />
              <Input
                label="Min total ($)"
                type="number"
                min={0}
                step="0.01"
                placeholder="0.00"
                value={totalMin}
                onChange={(e) => setFilter('totalMin', e.target.value)}
              />
              <Input
                label="Max total ($)"
                type="number"
                min={0}
                step="0.01"
                placeholder="999.99"
                value={totalMax}
                onChange={(e) => setFilter('totalMax', e.target.value)}
              />
            </div>
            {activeFilterCount > 0 && (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className="text-xs text-text-tertiary">Active:</span>
                {status && (
                  <FilterChip
                    label={`Status: ${ORDER_STATUSES.find((s) => s.value === status)?.label ?? status}`}
                    onRemove={() => setFilter('status', '')}
                  />
                )}
                {source && (
                  <FilterChip
                    label={`Source: ${SOURCE_OPTIONS.find((s) => s.value === source)?.label ?? source}`}
                    onRemove={() => setFilter('source', '')}
                  />
                )}
                {connectionId && (
                  <FilterChip
                    label={`Store: ${storeLabel}`}
                    onRemove={() => setFilter('connectionId', '')}
                  />
                )}
                {fulfillmentRequested && (
                  <FilterChip
                    label={`Fulfillment: ${fulfillmentRequested === 'true' ? 'Requested' : 'Not requested'}`}
                    onRemove={() => setFilter('fulfillmentRequested', '')}
                  />
                )}
                {tag && (
                  <FilterChip
                    label={`Tag: ${tag}`}
                    onRemove={() => setFilter('tag', '')}
                  />
                )}
                {dateFrom && (
                  <FilterChip
                    label={`From: ${dateFrom}`}
                    onRemove={() => setFilter('dateFrom', '')}
                  />
                )}
                {dateTo && (
                  <FilterChip
                    label={`To: ${dateTo}`}
                    onRemove={() => setFilter('dateTo', '')}
                  />
                )}
                {totalMin && (
                  <FilterChip
                    label={`Min: $${totalMin}`}
                    onRemove={() => setFilter('totalMin', '')}
                  />
                )}
                {totalMax && (
                  <FilterChip
                    label={`Max: $${totalMax}`}
                    onRemove={() => setFilter('totalMax', '')}
                  />
                )}
                <button
                  type="button"
                  onClick={clearAllFilters}
                  className="text-xs font-medium text-brand-600 hover:underline dark:text-brand-400"
                >
                  Clear all
                </button>
              </div>
            )}
          </div>
        )}

        {/* Table */}
        {ordersQuery.isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Spinner size="lg" />
          </div>
        ) : ordersQuery.isError ? (
          <div className="flex flex-col items-center justify-center gap-3 py-20">
            <p className="text-sm text-text-secondary">Failed to load orders.</p>
            <Button variant="outline" size="sm" onClick={() => ordersQuery.refetch()}>
              Retry
            </Button>
          </div>
        ) : orders.length === 0 ? (
          <div className="py-20 text-center">
            <p className="text-sm text-text-secondary">No orders found.</p>
            <p className="mt-1 text-xs text-text-tertiary">
              {search || status || dateFrom || dateTo || source || totalMin || totalMax
                ? 'Try adjusting your search or filters.'
                : 'Create your first order to get started.'}
            </p>
            {!search && !status && !dateFrom && !dateTo && !source && !totalMin && !totalMax && (
              <Link to="/orders/new" className="mt-3 inline-block">
                <Button variant="primary" size="sm">
                  Create Order
                </Button>
              </Link>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-surface-secondary/40">
                  <th className="px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
                    Order
                  </th>
                  <th className="px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
                    Customer
                  </th>
                  <th className="px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
                    Status
                  </th>
                  <th className="hidden px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-text-tertiary sm:table-cell">
                    Tags
                  </th>
                  <th className="hidden px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-text-tertiary md:table-cell">
                    Store
                  </th>
                  <th className="hidden px-5 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-text-tertiary sm:table-cell">
                    Items
                  </th>
                  <th className="px-5 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
                    Total
                  </th>
                  <th className="hidden px-5 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-text-tertiary md:table-cell">
                    TK cost
                  </th>
                  <th className="hidden px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-text-tertiary md:table-cell">
                    Settlement
                  </th>
                  <th className="hidden px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-text-tertiary lg:table-cell">
                    Date
                  </th>
                  <th className="px-5 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {orders.map((o) => (
                  <tr
                    key={o.id}
                    className="cursor-pointer transition-colors hover:bg-surface-secondary/60"
                    onClick={() => navigate(`/orders/${o.id}`)}
                  >
                    <td className="px-5 py-2.5 font-medium text-brand-600 whitespace-nowrap">
                      {o.orderNumber}
                    </td>
                    <td className="px-5 py-2.5 truncate max-w-[160px] text-text-primary">
                      {o.customerName ?? '—'}
                    </td>
                    <td className="px-5 py-2.5 whitespace-nowrap">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <Badge
                          variant={
                            ORDER_STATUS_MERCHANT_VARIANT[o.status] ?? 'default'
                          }
                          size="sm"
                        >
                          {getMerchantOrderStatusLabel(o.status)}
                        </Badge>
                        {o.fulfillmentMix === 'ALL_SELF' && (
                          <Badge variant="warning" size="sm">
                            SELF-FULFILL
                          </Badge>
                        )}
                        {o.fulfillmentMix === 'MIXED' && (
                          <Badge variant="info" size="sm">
                            MIXED FULFILLMENT
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="hidden px-5 py-2.5 sm:table-cell">
                      {/* First tag inline, the rest behind the "+N" dropdown.
                          Clicking a chip applies the tag filter. */}
                      <TagOverflowList
                        maxVisible={1}
                        groups={[
                          {
                            key: 'tags',
                            tags: o.tags ?? [],
                            tone: 'neutral',
                            onTagClick: (t) => setFilter('tag', t),
                          },
                        ]}
                      />
                    </td>
                    <td className="hidden px-5 py-2.5 truncate max-w-[160px] text-text-secondary md:table-cell">
                      {o.store?.name ?? <span className="text-text-tertiary">Manual</span>}
                    </td>
                    <td className="hidden px-5 py-2.5 text-right whitespace-nowrap text-text-secondary sm:table-cell">
                      {o.itemCount}
                    </td>
                    <td className="px-5 py-2.5 text-right whitespace-nowrap font-medium text-text-primary">
                      ${o.total}
                    </td>
                    <td
                      className="hidden px-5 py-2.5 text-right whitespace-nowrap text-text-secondary md:table-cell"
                      title="Wholesale cost billed to you by Transfer Kingdom"
                    >
                      {o.wholesaleTotalCost ? `$${o.wholesaleTotalCost}` : '—'}
                    </td>
                    <td className="hidden px-5 py-2.5 whitespace-nowrap md:table-cell">
                      {o.wholesaleChargeStatus &&
                      o.wholesaleChargeStatus !== WholesaleChargeStatus.NONE ? (
                        <SettlementBadge status={o.wholesaleChargeStatus} />
                      ) : (
                        <span className="text-text-tertiary">—</span>
                      )}
                    </td>
                    <td className="hidden px-5 py-2.5 whitespace-nowrap text-[12px] text-text-tertiary lg:table-cell">
                      {new Date(o.createdAt).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </td>
                    <td className="px-5 py-2.5 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/orders/${o.id}`)}
                      >
                        View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination != null && pagination.totalPages > 1 && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-5 py-3">
            <span className="text-xs text-text-tertiary">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <div className="flex gap-1.5">
              <button
                type="button"
                className="rounded-md border border-border bg-white px-3 py-1.5 text-xs font-medium text-text-primary transition-colors hover:bg-surface-secondary disabled:pointer-events-none disabled:opacity-50"
                disabled={page <= 1}
                onClick={() => setFilter('page', String(page - 1))}
              >
                Previous
              </button>
              <button
                type="button"
                className="rounded-md border border-border bg-white px-3 py-1.5 text-xs font-medium text-text-primary transition-colors hover:bg-surface-secondary disabled:pointer-events-none disabled:opacity-50"
                disabled={page >= pagination.totalPages}
                onClick={() => setFilter('page', String(page + 1))}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
